import { useState } from "react";
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import usePartShopifyUrl from "../../data/usePartShopifyUrl";
import { IPart } from "../../models/Part";
import {
  IMarketplaceListingSummary,
  Marketplace,
  MarketplaceListingStatus,
  marketplaceLabel,
} from "../../models/Publishing";
import getLiveEbayItemNumber from "../../utils/getLiveEbayItemNumber";
import openInNewTab from "../../utils/openInNewTab";
import Tooltip from "./Tooltip";

// Pill order on screen; matches the Marketplace enum.
const ORDER: Marketplace[] = [Marketplace.Ebay, Marketplace.PartPro, Marketplace.Shopify];

const styles: Record<number, string> = {
  [MarketplaceListingStatus.Live]: "bg-green-100 text-green-700 hover:bg-green-200",
  [MarketplaceListingStatus.Sold]: "bg-green-100 text-green-700 hover:bg-green-200",
  [MarketplaceListingStatus.Publishing]: "bg-amber-100 text-amber-700",
  [MarketplaceListingStatus.Failed]: "bg-red-100 text-red-700 hover:bg-red-200",
};

/**
 * Merges the per-marketplace rows with the older single-flag signals the rest of the app still
 * writes (offer.ebayId / isPublishedOnShopify). The rows win where present: they are the only
 * source that knows about a failed or in-flight attempt, or about PartPro at all. The legacy
 * signals fill the gap for parts published before the rows existed.
 */
export function resolveMarketplaceStatuses(part: IPart): IMarketplaceListingSummary[] {
  const byMarketplace = new Map<Marketplace, IMarketplaceListingSummary>();

  (part.marketplaceListings ?? []).forEach((row) => byMarketplace.set(row.marketplace, row));

  const liveEbay = getLiveEbayItemNumber(part);
  const ebayRow = byMarketplace.get(Marketplace.Ebay);
  if (liveEbay && (!ebayRow || ebayRow.status === MarketplaceListingStatus.NotListed)) {
    byMarketplace.set(Marketplace.Ebay, {
      marketplace: Marketplace.Ebay,
      status: MarketplaceListingStatus.Live,
      externalId: liveEbay,
      executedBySync: false,
    });
  } else if (ebayRow?.status === MarketplaceListingStatus.Live && liveEbay) {
    // The offer's ebayId is refreshed by the sync jobs; prefer it for the link.
    byMarketplace.set(Marketplace.Ebay, { ...ebayRow, externalId: liveEbay });
  }

  if (part.isPublishedOnShopify && !byMarketplace.has(Marketplace.Shopify)) {
    byMarketplace.set(Marketplace.Shopify, {
      marketplace: Marketplace.Shopify,
      status: MarketplaceListingStatus.Live,
      executedBySync: false,
    });
  }

  return ORDER.map((m) => byMarketplace.get(m)).filter((r): r is IMarketplaceListingSummary => !!r);
}

const isShown = (row: IMarketplaceListingSummary) =>
  row.status === MarketplaceListingStatus.Live ||
  row.status === MarketplaceListingStatus.Sold ||
  row.status === MarketplaceListingStatus.Publishing ||
  row.status === MarketplaceListingStatus.Failed;

export default function MarketplacePills({ part }: { part: IPart }) {
  const [fetchingShopify, setFetchingShopify] = useState(false);
  const { fetchShopifyUrl } = usePartShopifyUrl(part.id);

  const rows = resolveMarketplaceStatuses(part).filter(isShown);

  if (rows.length === 0) return null;

  const openListing = async (row: IMarketplaceListingSummary) => {
    if (row.listingUrl) return openInNewTab(row.listingUrl);

    if (row.marketplace === Marketplace.Ebay && row.externalId) {
      return openInNewTab(`https://www.ebay.co.uk/itm/${row.externalId}`);
    }

    if (row.marketplace === Marketplace.Shopify) {
      setFetchingShopify(true);
      const url = await fetchShopifyUrl();
      setFetchingShopify(false);
      if (url) openInNewTab(url);
    }
  };

  return (
    <div className="flex items-center gap-1 mt-1.5" onClick={(e) => e.stopPropagation()}>
      {rows.map((row) => {
        const label = marketplaceLabel[row.marketplace] ?? `Marketplace ${row.marketplace}`;
        const isLive = row.status === MarketplaceListingStatus.Live || row.status === MarketplaceListingStatus.Sold;
        const isFailed = row.status === MarketplaceListingStatus.Failed;
        const isPublishing = row.status === MarketplaceListingStatus.Publishing;
        const canOpen = isLive && (row.listingUrl || row.externalId || row.marketplace === Marketplace.Shopify);
        const busy = row.marketplace === Marketplace.Shopify && fetchingShopify;

        const pill = (
          <button
            type="button"
            key={row.marketplace}
            onClick={(e) => {
              e.stopPropagation();
              if (canOpen) openListing(row);
            }}
            disabled={busy || (!canOpen && !isFailed)}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-colors disabled:opacity-80 disabled:cursor-default ${
              styles[row.status] ?? "bg-gray-100 text-gray-600"
            }`}
          >
            {isPublishing && <ArrowPathIcon className="h-3 w-3 animate-spin" aria-hidden="true" />}
            {isFailed && <ExclamationTriangleIcon className="h-3 w-3" aria-hidden="true" />}
            {isLive && row.executedBySync && <CheckCircleIcon className="h-3 w-3" aria-hidden="true" />}
            {busy ? "…" : label}
            {row.status === MarketplaceListingStatus.Sold && <span className="opacity-70">· sold</span>}
          </button>
        );

        if (isFailed) {
          return (
            <Tooltip key={row.marketplace} content={row.lastError || `${label} publish failed`}>
              {pill}
            </Tooltip>
          );
        }

        if (isPublishing) {
          return (
            <Tooltip key={row.marketplace} content={`Publishing to ${label}…`}>
              {pill}
            </Tooltip>
          );
        }

        return pill;
      })}
    </div>
  );
}
